import { clsx } from 'clsx'
import dynamic from 'next/dynamic'
import { type FC } from 'react'

import { DialogContent, Modal } from 'base-ui'

import { DrawerTransition } from '../../drawer-transition'
import { useGlobalConfigContext } from '../../global-config'
import {
  RECOMMENDATION_WIDGET_ADD_TO_CART_BOTTOM,
  RECOMMENDATION_WIDGET_ADD_TO_CART_MIDDLE,
  RECOMMENDATION_WIDGET_ADD_TO_CART_TOP,
} from '../../recommendation'
import { TENANT } from '../../tenant'
import { useCartIntermediateDrawerResendInitialPdpGtmValue } from '../gtm/useCartIntermediateDrawerResendInitialPdpGtmValue'
import { useCartIntermediateContext } from '../hooks/useCartIntermediateContext'
import { useCartIntermediateDrawerFetchGtmDataAndPushToDataLayer } from '../hooks/useCartIntermediateDrawerFetchGtmDataAndPushToDataLayer'

import { CartIntermediateMessage } from './CartIntermediateLabels/CartIntermediateMessage'
import { CartIntermediateTitle } from './CartIntermediateLabels/CartIntermediateTitle'
import { CartIntermediateProductWithButtonsSection } from './CartIntermediateProductWithButtonsSection/CartIntermediateProductWithButtonsSection'

import styles from './CartIntermediateDrawer.module.css'

const CartIntermediateRecommendationWidgetSlider = dynamic(async () =>
  import('./CartIntermediateRecommendationAreaSliderWidget/CartIntermediateRecommendationAreaSlider').then(
    (mod) => mod.CartIntermediateRecommendationWidgetSlider,
  ),
)

const CartIntermediateSpecialOffersRecommendationWidgetContent = dynamic(
  async () =>
    import('./CartIntermediateSpecialOffersRecommendationArea/CartIntermediateSpecialOffersRecommendationAreaContent').then(
      (mod) => mod.CartIntermediateSpecialOffersRecommendationWidgetContent,
    ),
)

export const CartIntermediateDrawer: FC = () => {
  const {
    cartIntermediateOpenableControls: { handleClose: closeDrawer, isOpen },
    specialOffersCollapsibleControls,
  } = useCartIntermediateContext()
  const { tenant } = useGlobalConfigContext()

  // when drawer is closed we need to resend initial PDP GTM data
  const resendInitialPdpGtmValue =
    useCartIntermediateDrawerResendInitialPdpGtmValue()
  const handleClose = (): void => {
    void resendInitialPdpGtmValue()
    closeDrawer()
  }
  useCartIntermediateDrawerFetchGtmDataAndPushToDataLayer()

  return (
    <Modal
      className={clsx(
        styles['cart-intermediate-drawer'],
        'flex flex-col items-end justify-end rounded-l-m',
      )}
      closeAfterTransition
      data-qa-id="cartIntermediateDrawer"
      onClose={handleClose}
      open={isOpen}
    >
      <DrawerTransition
        // eslint-disable-next-line tailwindcss/no-arbitrary-value -- don't reference, refactor if touched
        className="mt-[120px] flex h-dvh w-full flex-col overflow-y-auto rounded-t-m bg-light-primary-medium outline-none tablet:mt-0 tablet:max-w-screen-tablet tablet:rounded-l-m tablet:rounded-tr-none"
        in={isOpen}
      >
        {specialOffersCollapsibleControls.isOpen ? (
          <CartIntermediateSpecialOffersRecommendationWidgetContent />
        ) : (
          <>
            <CartIntermediateTitle handleClose={handleClose} />
            <DialogContent>
              <CartIntermediateMessage />
              <CartIntermediateProductWithButtonsSection
                handleClose={handleClose}
              />
              <CartIntermediateRecommendationWidgetSlider
                productsPerPage={4}
                widgetType={RECOMMENDATION_WIDGET_ADD_TO_CART_TOP}
              />
              <CartIntermediateRecommendationWidgetSlider
                productsPerPage={4}
                widgetType={RECOMMENDATION_WIDGET_ADD_TO_CART_MIDDLE}
              />
              {!new Set<TENANT>([TENANT.BE, TENANT.CH, TENANT.IT]).has(
                tenant,
              ) && (
                <CartIntermediateRecommendationWidgetSlider
                  productsPerPage={4}
                  widgetType={RECOMMENDATION_WIDGET_ADD_TO_CART_BOTTOM}
                />
              )}
            </DialogContent>
          </>
        )}
      </DrawerTransition>
    </Modal>
  )
}
